import React, { useCallback } from 'react';
import { Drawer, Box, Typography, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { IRecipe } from '../../api/IRecipe';
import { CREATE_RECIPE } from '../../routes';
import { useAIAssistant } from '../../hooks/AIAssistantContext';
import AssistantActionMenu from './AssistantActionMenu';
import RecipeAssistant from './RecipeAssistant';

interface AIAssistantDrawerProps {
  open: boolean;
  onClose: () => void;
  onPopulateRecipeForm?: (proposal: Partial<IRecipe>) => void;
  getCurrentRecipe?: () => Partial<IRecipe> | undefined;
}

const AIAssistantDrawer: React.FC<AIAssistantDrawerProps> = ({
  open,
  onClose,
  onPopulateRecipeForm,
  getCurrentRecipe,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { drawerMode, setDrawerMode } = useAIAssistant();

  const handleSelectRecipe = useCallback(() => {
    setDrawerMode('recipe');
    if (!location.pathname.startsWith(CREATE_RECIPE)) {
      navigate(CREATE_RECIPE);
    }
  }, [navigate, location.pathname, setDrawerMode]);

  const handleSelectSearch = useCallback(() => {
    setDrawerMode('search');
  }, [setDrawerMode]);

  const handleBack = useCallback(() => {
    setDrawerMode('menu');
  }, [setDrawerMode]);

  const handleProposal = useCallback(
    (proposal: Partial<IRecipe>) => {
      onPopulateRecipeForm?.(proposal);
    },
    [onPopulateRecipeForm]
  );

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { width: { xs: '100%', sm: 400 }, p: 2, display: 'flex', flexDirection: 'column' },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        {drawerMode !== 'menu' && (
          <IconButton onClick={handleBack} size="small" sx={{ mr: 1 }}>
            <ArrowBackIcon />
          </IconButton>
        )}
        <AutoAwesomeIcon color="primary" sx={{ mr: 1 }} />
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          {t('aiAssistant.title')}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      <Box sx={{ flexGrow: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {drawerMode === 'menu' && (
          <AssistantActionMenu
            onSelectRecipe={handleSelectRecipe}
            onSelectSearch={handleSelectSearch}
          />
        )}

        {drawerMode === 'recipe' && (
          <RecipeAssistant onProposal={handleProposal} getCurrentRecipe={getCurrentRecipe} />
        )}

        {drawerMode === 'search' && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
            {t('search.startTyping')}
          </Typography>
        )}
      </Box>
    </Drawer>
  );
};

export default AIAssistantDrawer;
